import { API_URL, TOKEN_KEY } from '../config';

export class ApiError extends Error {
  /** `fields` is {field: message} when the server says which inputs are wrong. */
  constructor(message, status = 0, fields = null) {
    super(message);
    this.status = status;
    this.fields = fields;
  }
}

export const tokenStore = {
  get: () => sessionStorage.getItem(TOKEN_KEY),
  set: (token) => sessionStorage.setItem(TOKEN_KEY, token),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
};

const toText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(toText).join(' ');
  if (typeof value === 'object') return value.trace ? toText(value.trace) : Object.values(value).map(toText).join(' ');
  return String(value);
};

const query = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    // Arrays are sent as repeated keys (?lead=LI&lead=HC), which the server reads as a list.
    (Array.isArray(value) ? value : [value]).forEach((item) => {
      if (item !== undefined && item !== null && item !== '') q.append(key, item);
    });
  });
  const text = q.toString();
  return text ? `?${text}` : '';
};

async function request(method, path, { params, body, form, signal } = {}) {
  const headers = { Accept: 'application/json' };
  const token = tokenStore.get();
  if (token) headers.Authorization = `Token ${token}`;
  let payload;
  if (form) payload = form;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(`${API_URL}${path}${query(params)}`, { method, headers, body: payload, signal });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new ApiError('Cannot reach the server. Check your connection and try again.');
  }

  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    data = null;
  }

  if (response.status === 401 && token) {
    tokenStore.clear();
    window.dispatchEvent(new Event('ats:unauthorized'));
  }
  if (!response.ok) {
    throw new ApiError(toText(data && (data.message || data.detail)) || `Request failed (${response.status}).`,
      response.status, data && data.fields);
  }
  // Older endpoints report failures inside a 200 response.
  if (data && (data.statusFlag === false || data.response_code >= 400)) {
    throw new ApiError(toText(data.message || data.errorDetails) || 'The request failed.', data.response_code || 400);
  }
  return data;
}

/** Download a file from an authenticated endpoint and hand it to the browser as a save dialog. */
async function download(path, params, filename) {
  const token = tokenStore.get();
  let response;
  try {
    response = await fetch(`${API_URL}${path}${query(params)}`, { headers: token ? { Authorization: `Token ${token}` } : {} });
  } catch (error) {
    throw new ApiError('Cannot reach the server. Check your connection and try again.');
  }
  if (!response.ok) {
    if (response.status === 401 && token) {
      tokenStore.clear();
      window.dispatchEvent(new Event('ats:unauthorized'));
    }
    let body = null;
    try { body = await response.json(); } catch (error) { body = null; }
    throw new ApiError(toText(body && (body.message || body.detail)) || `Export failed (${response.status}).`, response.status);
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const api = {
  download,
  get: (path, params, signal) => request('GET', path, { params, signal }),
  post: (path, body) => request('POST', path, { body }),
  patch: (path, body) => request('PATCH', path, { body }),
  delete: (path) => request('DELETE', path),
  upload: (path, form) => request('POST', path, { form }),
};
